import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBarcode,
  faPlus,
  faSearch,
  faTable,
  faThLarge,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'

type TallyToolbarProps = {
  isAssetTab: boolean
  /** Overrides placeholder / aria when set (e.g. fleet). */
  searchMode?: 'assets' | 'inventory' | 'fleet'
  search: string
  onSearchChange: (value: string) => void
  onSearchCommit: (value: string) => void
  filters: string[]
  onRemoveFilter: (index: number) => void
  onClearSearch?: () => void
  resultCount?: number
  totalCount?: number
  collapsedView: boolean
  onToggleCollapsedView: () => void
  canManage: boolean
  onAdd: () => void
  onScan?: () => void
  showScan?: boolean
  hideViewToggle?: boolean
  trailing?: React.ReactNode
}

export const TallyToolbar: React.FC<TallyToolbarProps> = ({
  isAssetTab,
  searchMode,
  search,
  onSearchChange,
  onSearchCommit,
  filters,
  onRemoveFilter,
  onClearSearch,
  resultCount,
  totalCount,
  collapsedView,
  onToggleCollapsedView,
  canManage,
  onAdd,
  onScan,
  showScan = false,
  hideViewToggle = false,
  trailing,
}) => {
  const mode = searchMode || (isAssetTab ? 'assets' : 'inventory')
  const placeholder =
    mode === 'fleet'
      ? 'Search name, VIN, year, make, model, status, Linxup…'
      : mode === 'assets'
        ? 'Search name, type, location, employee, serial, status…'
        : 'Search name, barcode, location, employee, status, SKU, notes…'
  const searchAria =
    mode === 'fleet' ? 'Search fleet' : mode === 'assets' ? 'Search assets' : 'Search inventory'
  const hasQuery = Boolean(search.trim() || filters.length)
  const showingFiltered =
    typeof resultCount === 'number' && typeof totalCount === 'number' && hasQuery

  return (
    <div className="mb-3 flex flex-col gap-2 w-full shrink-0">
      <div className="flex items-center gap-2 w-full">
        <label className="input input-bordered flex items-center gap-2 min-w-0 flex-1 min-h-11">
          <FontAwesomeIcon icon={faSearch} className="text-base-content/60 shrink-0" />
          <input
            type="text"
            className="grow min-w-0"
            placeholder={placeholder}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && search.trim()) {
                onSearchCommit(search.trim())
              }
              if (e.key === 'Escape' && (search || filters.length)) {
                onClearSearch?.()
              }
            }}
            aria-label={searchAria}
          />
          {search ? (
            <button
              type="button"
              className="btn btn-ghost btn-xs btn-circle shrink-0"
              aria-label="Clear search"
              onClick={() => onSearchChange('')}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          ) : null}
        </label>

        {trailing}

        {!hideViewToggle ? (
          <button
            type="button"
            className="btn btn-outline h-11 min-h-0 w-11 shrink-0 rounded-box px-0 sm:w-auto sm:px-3"
            title={collapsedView ? 'Card view' : 'Table view'}
            onClick={onToggleCollapsedView}
          >
            <FontAwesomeIcon icon={collapsedView ? faThLarge : faTable} />
            <span className="hidden sm:inline">{collapsedView ? 'Cards' : 'Table'}</span>
          </button>
        ) : null}

        {canManage ? (
          <button
            type="button"
            className="btn btn-primary h-11 min-h-0 rounded-box px-3 hidden sm:inline-flex"
            title={isAssetTab ? 'Add Asset' : 'Add Inventory'}
            onClick={onAdd}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>{isAssetTab ? 'Add Asset' : 'Add Inventory'}</span>
          </button>
        ) : null}

        {showScan && onScan ? (
          <button
            type="button"
            className="btn btn-outline h-11 min-h-0 rounded-box px-3 hidden sm:inline-flex"
            title="Scan barcode"
            onClick={onScan}
          >
            <FontAwesomeIcon icon={faBarcode} />
            <span>Scan</span>
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2 min-h-5">
        {filters.length > 0 ? (
          <>
            {filters.map((filter, idx) => (
              <button
                key={`${filter}-${idx}`}
                type="button"
                className="badge badge-info gap-2 cursor-pointer h-7"
                onClick={() => onRemoveFilter(idx)}
                title="Remove filter"
              >
                {filter}{' '}
                <FontAwesomeIcon icon={faTimes} className="text-[10px]" aria-hidden />
              </button>
            ))}
            {onClearSearch ? (
              <button type="button" className="btn btn-ghost btn-xs" onClick={onClearSearch}>
                Clear all
              </button>
            ) : null}
          </>
        ) : (
          <p className="text-xs text-base-content/50">
            Type to filter · Enter pins a term · Esc clears
          </p>
        )}
        {showingFiltered ? (
          <span className="text-xs text-base-content/60 ml-auto">
            {resultCount} of {totalCount}
          </span>
        ) : null}
      </div>
    </div>
  )
}

export default TallyToolbar
